import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCode, FiSmartphone, FiBarChart, FiZap, FiPalette, FiLink, FiShoppingCart, FiGlobe, FiUser, FiDatabase, FiFileText, FiMusic, FiCamera, FiTool, FiHeart, FiBook, FiDollarSign, FiTrendingUp, FiCpu, FiSettings, FiMessageCircle, FiCalendar, FiMap, FiGamepad2, FiVideo, FiMail, FiLayers, FiTarget, FiLock, FiCloud, FiMonitor, FiPackage, FiPieChart, FiActivity, FiWifi } = FiIcons;

const PromptCategories = ({ onSelectTemplate }) => {
  const categories = [
    {
      id: 'web-development',
      name: 'Web Development',
      icon: FiCode,
      description: 'Modern web applications and websites',
      templates: [
        {
          name: 'Progressive Web App',
          data: {
            category: 'web-development',
            projectType: 'react-app',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: true,
            features: ['responsive', 'animations'],
            customRequirements: 'App shell architecture, offline functionality, push notifications, installable experience, background sync, responsive design'
          }
        },
        {
          name: 'E-commerce Store',
          data: {
            category: 'web-development',
            projectType: 'e-commerce',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'search', 'filters', 'auth', 'database'],
            customRequirements: 'Product catalog, shopping cart, payment integration, user accounts, order management, inventory tracking'
          }
        },
        {
          name: 'Portfolio Website',
          data: {
            category: 'web-development',
            projectType: 'portfolio',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'dark-mode'],
            customRequirements: 'Showcase projects, contact form, smooth scrolling, modern design, testimonials section'
          }
        },
        {
          name: 'SaaS Landing Page',
          data: {
            category: 'web-development',
            projectType: 'landing-page',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth'],
            customRequirements: 'Hero section, pricing tables, feature highlights, testimonials, call-to-action buttons'
          }
        },
        {
          name: 'Blog Platform',
          data: {
            category: 'web-development',
            projectType: 'blog',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'search', 'filters', 'auth', 'database'],
            customRequirements: 'Article management, categories, tags, comments, SEO optimization, admin panel'
          }
        }
      ]
    },
    {
      id: 'mobile-app',
      name: 'Mobile Apps',
      icon: FiSmartphone,
      description: 'Touch-friendly, responsive mobile experiences',
      templates: [
        {
          name: 'Mobile PWA',
          data: {
            category: 'mobile-app',
            projectType: 'react-app',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: true,
            features: ['responsive', 'animations', 'touch-gestures'],
            customRequirements: 'Mobile-first design, touch gestures, offline functionality, push notifications, app-like experience, installable on mobile devices'
          }
        },
        {
          name: 'Task Manager',
          data: {
            category: 'mobile-app',
            projectType: 'tool',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Todo lists, categories, due dates, priority levels, offline support, notifications'
          }
        },
        {
          name: 'Social Media App',
          data: {
            category: 'mobile-app',
            projectType: 'social',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database', 'api'],
            customRequirements: 'User profiles, news feed, messaging, image sharing, likes, comments, followers'
          }
        },
        {
          name: 'Fitness Tracker',
          data: {
            category: 'mobile-app',
            projectType: 'fitness',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database', 'api'],
            customRequirements: 'Workout tracking, progress charts, goal setting, exercise library, nutrition tracking'
          }
        }
      ]
    },
    {
      id: 'data-analysis',
      name: 'Data Analysis',
      icon: FiBarChart,
      description: 'Charts, visualizations, and data processing',
      templates: [
        {
          name: 'Analytics Dashboard',
          data: {
            category: 'data-analysis',
            projectType: 'dashboard',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'api', 'filters'],
            customRequirements: 'Interactive charts, data export, real-time updates, custom date ranges, KPI metrics'
          }
        },
        {
          name: 'Financial Dashboard',
          data: {
            category: 'data-analysis',
            projectType: 'finance',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database', 'api'],
            customRequirements: 'Portfolio tracking, stock charts, expense analysis, budget planning, investment insights'
          }
        },
        {
          name: 'Sales Analytics',
          data: {
            category: 'data-analysis',
            projectType: 'dashboard',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'filters', 'api'],
            customRequirements: 'Sales reports, conversion funnels, customer analytics, revenue tracking, forecasting'
          }
        }
      ]
    },
    {
      id: 'automation',
      name: 'Automation Tools',
      icon: FiZap,
      description: 'Workflow automation and productivity tools',
      templates: [
        {
          name: 'Workflow Builder',
          data: {
            category: 'automation',
            projectType: 'tool',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database', 'api'],
            customRequirements: 'Drag-and-drop interface, step configuration, execution monitoring, integrations'
          }
        },
        {
          name: 'Form Builder',
          data: {
            category: 'automation',
            projectType: 'tool',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Dynamic form creation, validation rules, conditional logic, data collection'
          }
        },
        {
          name: 'Email Automation',
          data: {
            category: 'automation',
            projectType: 'tool',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database', 'api'],
            customRequirements: 'Email templates, campaign management, subscriber lists, analytics, scheduling'
          }
        }
      ]
    },
    {
      id: 'ui-design',
      name: 'UI/UX Design',
      icon: FiPalette,
      description: 'Design systems and component libraries',
      templates: [
        {
          name: 'Component Library',
          data: {
            category: 'ui-design',
            projectType: 'tool',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'dark-mode'],
            customRequirements: 'Reusable components, documentation, interactive examples, theme customization'
          }
        },
        {
          name: 'Design Portfolio',
          data: {
            category: 'ui-design',
            projectType: 'portfolio',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'dark-mode'],
            customRequirements: 'Project showcase, case studies, design process, client testimonials, contact form'
          }
        }
      ]
    },
    {
      id: 'api-integration',
      name: 'API Integration',
      icon: FiLink,
      description: 'External APIs and data integrations',
      templates: [
        {
          name: 'API Explorer',
          data: {
            category: 'api-integration',
            projectType: 'tool',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'api', 'search'],
            customRequirements: 'API testing interface, request/response viewer, authentication, documentation'
          }
        },
        {
          name: 'Weather App',
          data: {
            category: 'api-integration',
            projectType: 'react-app',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'api'],
            customRequirements: 'Current weather, forecasts, location search, weather maps, alerts'
          }
        }
      ]
    },
    {
      id: 'e-commerce',
      name: 'E-commerce',
      icon: FiShoppingCart,
      description: 'Online stores and marketplace solutions',
      templates: [
        {
          name: 'Multi-vendor Marketplace',
          data: {
            category: 'e-commerce',
            projectType: 'marketplace',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'search', 'filters', 'auth', 'database'],
            customRequirements: 'Vendor management, product listings, order processing, payment gateway, reviews'
          }
        },
        {
          name: 'Fashion Store',
          data: {
            category: 'e-commerce',
            projectType: 'e-commerce',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'search', 'filters', 'auth'],
            customRequirements: 'Product gallery, size guide, wishlist, shopping cart, checkout process'
          }
        }
      ]
    },
    {
      id: 'content-management',
      name: 'Content Management',
      icon: FiFileText,
      description: 'CMS and content publishing platforms',
      templates: [
        {
          name: 'News Portal',
          data: {
            category: 'content-management',
            projectType: 'blog',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'search', 'filters', 'auth', 'database'],
            customRequirements: 'Article management, categories, breaking news, editorial workflow, comments'
          }
        },
        {
          name: 'Knowledge Base',
          data: {
            category: 'content-management',
            projectType: 'blog',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'search', 'filters'],
            customRequirements: 'Article hierarchy, search functionality, categories, user guides, FAQ section'
          }
        }
      ]
    },
    {
      id: 'entertainment',
      name: 'Entertainment',
      icon: FiMusic,
      description: 'Games, media, and entertainment apps',
      templates: [
        {
          name: 'Music Player',
          data: {
            category: 'entertainment',
            projectType: 'music',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Playlist management, audio controls, music library, shuffle, repeat modes'
          }
        },
        {
          name: 'Video Streaming',
          data: {
            category: 'entertainment',
            projectType: 'video',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'Video player, content library, user profiles, watchlist, recommendations'
          }
        },
        {
          name: 'Puzzle Game',
          data: {
            category: 'entertainment',
            projectType: 'game',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations'],
            customRequirements: 'Game logic, scoring system, levels, high scores, responsive controls'
          }
        }
      ]
    },
    {
      id: 'productivity',
      name: 'Productivity',
      icon: FiTool,
      description: 'Tools to boost productivity and efficiency',
      templates: [
        {
          name: 'Project Management',
          data: {
            category: 'productivity',
            projectType: 'tool',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'Task boards, team collaboration, time tracking, project timelines, file sharing'
          }
        },
        {
          name: 'Note Taking App',
          data: {
            category: 'productivity',
            projectType: 'notes',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'search', 'database'],
            customRequirements: 'Rich text editor, organization system, tags, search, sync across devices'
          }
        },
        {
          name: 'Time Tracker',
          data: {
            category: 'productivity',
            projectType: 'tool',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Time logging, project categories, reports, productivity insights, team tracking'
          }
        }
      ]
    },
    {
      id: 'health-fitness',
      name: 'Health & Fitness',
      icon: FiHeart,
      description: 'Health tracking and fitness applications',
      templates: [
        {
          name: 'Workout Planner',
          data: {
            category: 'health-fitness',
            projectType: 'fitness',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database', 'api'],
            customRequirements: 'Exercise library, workout plans, progress tracking, nutrition logging, goal setting'
          }
        },
        {
          name: 'Mental Health Tracker',
          data: {
            category: 'health-fitness',
            projectType: 'tool',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Mood tracking, meditation timer, journal entries, progress visualization, reminders'
          }
        }
      ]
    },
    {
      id: 'education',
      name: 'Education',
      icon: FiBook,
      description: 'Learning platforms and educational tools',
      templates: [
        {
          name: 'Online Course Platform',
          data: {
            category: 'education',
            projectType: 'learning',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database', 'api'],
            customRequirements: 'Course catalog, video player, progress tracking, quizzes, certificates, forums'
          }
        },
        {
          name: 'Quiz App',
          data: {
            category: 'education',
            projectType: 'quiz',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Question bank, multiple choice, scoring, time limits, leaderboards, categories'
          }
        },
        {
          name: 'Language Learning',
          data: {
            category: 'education',
            projectType: 'learning',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'Vocabulary practice, pronunciation, progress tracking, lessons, achievements'
          }
        }
      ]
    },
    {
      id: 'finance',
      name: 'Finance',
      icon: FiDollarSign,
      description: 'Financial management and fintech solutions',
      templates: [
        {
          name: 'Expense Tracker',
          data: {
            category: 'finance',
            projectType: 'finance',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Transaction logging, categories, budgets, reports, recurring expenses, goals'
          }
        },
        {
          name: 'Investment Portfolio',
          data: {
            category: 'finance',
            projectType: 'finance',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database', 'api'],
            customRequirements: 'Portfolio tracking, stock prices, performance charts, dividend tracking, alerts'
          }
        },
        {
          name: 'Cryptocurrency Tracker',
          data: {
            category: 'finance',
            projectType: 'dashboard',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'api'],
            customRequirements: 'Real-time prices, portfolio tracking, market charts, news feed, alerts'
          }
        }
      ]
    },
    {
      id: 'real-estate',
      name: 'Real Estate',
      icon: FiMap,
      description: 'Property management and real estate platforms',
      templates: [
        {
          name: 'Property Listing',
          data: {
            category: 'real-estate',
            projectType: 'real-estate',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'search', 'filters', 'database'],
            customRequirements: 'Property search, photo galleries, map integration, contact forms, saved searches'
          }
        },
        {
          name: 'Property Management',
          data: {
            category: 'real-estate',
            projectType: 'dashboard',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'Tenant management, rent collection, maintenance requests, lease tracking, reports'
          }
        }
      ]
    },
    {
      id: 'communication',
      name: 'Communication',
      icon: FiMessageCircle,
      description: 'Chat, messaging, and communication tools',
      templates: [
        {
          name: 'Chat Application',
          data: {
            category: 'communication',
            projectType: 'chat',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'Real-time messaging, group chats, file sharing, emoji support, online status'
          }
        },
        {
          name: 'Video Conference',
          data: {
            category: 'communication',
            projectType: 'video-call',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'api'],
            customRequirements: 'Video/audio calls, screen sharing, meeting rooms, scheduling, recording'
          }
        }
      ]
    },
    {
      id: 'calendar-events',
      name: 'Calendar & Events',
      icon: FiCalendar,
      description: 'Event management and calendar applications',
      templates: [
        {
          name: 'Event Management',
          data: {
            category: 'calendar-events',
            projectType: 'dashboard',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'Event creation, ticket sales, attendee management, check-in, analytics'
          }
        },
        {
          name: 'Appointment Booking',
          data: {
            category: 'calendar-events',
            projectType: 'booking',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Calendar integration, time slots, booking confirmation, reminders, cancellations'
          }
        }
      ]
    },
    {
      id: 'travel',
      name: 'Travel & Tourism',
      icon: FiMap,
      description: 'Travel planning and tourism applications',
      templates: [
        {
          name: 'Travel Planner',
          data: {
            category: 'travel',
            projectType: 'travel',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database', 'api'],
            customRequirements: 'Itinerary creation, destination search, booking integration, expense tracking, maps'
          }
        },
        {
          name: 'Hotel Booking',
          data: {
            category: 'travel',
            projectType: 'booking',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'search', 'filters', 'database'],
            customRequirements: 'Hotel search, availability calendar, room selection, booking process, reviews'
          }
        }
      ]
    },
    {
      id: 'gaming',
      name: 'Gaming',
      icon: FiGamepad2,
      description: 'Browser games and gaming platforms',
      templates: [
        {
          name: 'Card Game',
          data: {
            category: 'gaming',
            projectType: 'game',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations'],
            customRequirements: 'Game mechanics, card animations, scoring system, multiplayer support, leaderboards'
          }
        },
        {
          name: 'Trivia Game',
          data: {
            category: 'gaming',
            projectType: 'game',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database'],
            customRequirements: 'Question categories, time limits, scoring, multiplayer, difficulty levels'
          }
        }
      ]
    },
    {
      id: 'media-photography',
      name: 'Media & Photography',
      icon: FiCamera,
      description: 'Photo galleries and media management',
      templates: [
        {
          name: 'Photo Gallery',
          data: {
            category: 'media-photography',
            projectType: 'gallery',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'search'],
            customRequirements: 'Image upload, albums, lightbox, filters, sharing, metadata display'
          }
        },
        {
          name: 'Photography Portfolio',
          data: {
            category: 'media-photography',
            projectType: 'portfolio',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'dark-mode'],
            customRequirements: 'Photo showcase, client galleries, contact form, pricing, testimonials'
          }
        }
      ]
    },
    {
      id: 'security',
      name: 'Security & Privacy',
      icon: FiLock,
      description: 'Security tools and privacy applications',
      templates: [
        {
          name: 'Password Manager',
          data: {
            category: 'security',
            projectType: 'security',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'Password storage, encryption, generator, categories, secure sharing, 2FA'
          }
        },
        {
          name: 'VPN Dashboard',
          data: {
            category: 'security',
            projectType: 'dashboard',
            complexity: 'intermediate',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth'],
            customRequirements: 'Server selection, connection status, usage statistics, settings, location map'
          }
        }
      ]
    },
    {
      id: 'cloud-storage',
      name: 'Cloud & Storage',
      icon: FiCloud,
      description: 'File management and cloud storage solutions',
      templates: [
        {
          name: 'File Manager',
          data: {
            category: 'cloud-storage',
            projectType: 'file-manager',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'File upload, folder structure, sharing, permissions, search, preview'
          }
        },
        {
          name: 'Document Collaboration',
          data: {
            category: 'cloud-storage',
            projectType: 'tool',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'auth', 'database'],
            customRequirements: 'Real-time editing, version control, comments, sharing, permissions, templates'
          }
        }
      ]
    },
    {
      id: 'monitoring',
      name: 'Monitoring & Analytics',
      icon: FiMonitor,
      description: 'System monitoring and analytics platforms',
      templates: [
        {
          name: 'Server Monitor',
          data: {
            category: 'monitoring',
            projectType: 'monitoring',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'api'],
            customRequirements: 'Real-time metrics, alerts, uptime tracking, performance graphs, notifications'
          }
        },
        {
          name: 'Website Analytics',
          data: {
            category: 'monitoring',
            projectType: 'dashboard',
            complexity: 'advanced',
            framework: 'react',
            styling: 'tailwind',
            isPwa: false,
            features: ['responsive', 'animations', 'database', 'api'],
            customRequirements: 'Traffic analysis, user behavior, conversion tracking, reports, real-time data'
          }
        }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Project Categories</h2>
        <p className="text-slate-600">Choose from our comprehensive collection of project templates</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <SafeIcon icon={category.icon} className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{category.name}</h3>
                <p className="text-sm text-slate-600">{category.description}</p>
              </div>
            </div>

            <div className="space-y-3">
              {category.templates.map((template) => (
                <motion.div
                  key={template.name}
                  whileHover={{ scale: 1.02 }}
                  className="bg-slate-50 rounded-lg p-4 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => onSelectTemplate(template.data)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-slate-800">{template.name}</h4>
                    {template.data.isPwa && (
                      <SafeIcon icon={FiWifi} className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                    {template.data.customRequirements.substring(0, 80)}...
                  </p>
                  <div className="flex flex-wrap gap-1">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {template.data.complexity}
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      {template.data.framework}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                      {template.data.features.length} features
                    </span>
                    {template.data.isPwa && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        PWA
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                {category.templates.length} templates available
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Don't see what you need?</h3>
          <p className="text-slate-600 mb-4">
            You can always create a custom prompt by going back to the Prompt Builder and describing your unique project requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
              25+ Categories
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
              85+ Templates
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
              PWA Support
            </span>
            <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
              Production Ready
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptCategories;